import { atom } from 'jotai';

export type SalesLevel = 'rep' | 'manager';

/**
 * Sales access as reported by GET /api/v2/crm/me.
 *
 * `via: 'super'` means the user holds no order.sales_reps row and is treated as
 * a manager because their permission tier is SUPERADMIN/SUPERUSER, so the
 * per-rep sending fields are absent.
 */
export interface ISalesAccess {
    /** Who's signed in, to tell their conversations from a teammate's. */
    user_id?: number;
    level: SalesLevel;
    via: 'grant' | 'super';
    from_name?: string | null;
    from_email?: string | null;
    calendar_link?: string | null;
    daily_send_cap?: number;
    /** The rep's CRM mailbox on the sales mail domain; assigned on first send. */
    mailbox_address?: string | null;
    /** One line under their name in the email signature. */
    signature_title?: string | null;
    /** Their photo in the CRM: one they uploaded, or their Google photo. Null draws initials. */
    avatar_url?: string | null;
    /** Where email leaves from. Decided by the CRM: the rep's Gmail when connected, else the CRM mailbox. */
    sending_as?: { address: string | null; via: 'gmail' | 'mailbox' };
    /** Email signature: digits, spaces and + ( ) - . only. */
    phone?: string | null;
    /** https://www.linkedin.com/in/... */
    linkedin_url?: string | null;
    /** The words on the booking link, e.g. "Book 15 minutes". Null shows "Book a call". */
    booking_label?: string | null;
    /** Hosted round PNG for email, set by the signature photo endpoints. */
    signature_photo_url?: string | null;
    show_photo?: boolean;
}

/** What a rep may change about themselves (PUT /crm/me). */
export interface ISendingProfilePatch {
    from_name?: string | null;
    from_email?: string | null;
    calendar_link?: string | null;
    signature_title?: string | null;
    phone?: string | null;
    linkedin_url?: string | null;
    booking_label?: string | null;
    show_photo?: boolean;
}

/** A rep joined to their user identity, for the manager admin list. */
/** A user a manager can grant sales access to, from the type-ahead. */
export interface ISalesRepCandidate {
    id: number;
    email: string | null;
    first: string | null;
    last: string | null;
}

export interface ISalesRepListItem extends ISalesRep {
    email: string | null;
    first: string | null;
    last: string | null;
}

export interface ISalesRep {
    id?: number;
    user_id: number;
    level: SalesLevel;
    active: boolean;
    from_name?: string | null;
    from_email?: string | null;
    calendar_link?: string | null;
    daily_send_cap: number;
    granted_by?: number | null;
    created_at?: string;
    mailbox_address?: string | null;
}

/**
 * Resolution state for the current user's sales access.
 *
 * `status` is deliberately three-valued rather than a boolean plus a loading
 * flag: the nav must not flash the Sales tab before resolution, and must not
 * hide it permanently if the call merely failed.
 */
export type SalesAccessStatus = 'unresolved' | 'resolving' | 'granted' | 'denied' | 'error';

export interface ISalesAccessState {
    status: SalesAccessStatus;
    access: ISalesAccess | null;
}

export const salesAccessAtom = atom<ISalesAccessState>({
    status: 'unresolved',
    access: null
});

export type CrmSegment =
    | 'artist'
    | 'interior_designer'
    | 'wallpaper_brand'
    | 'retailer'
    | 'architect'
    | 'hospitality'
    | 'other'
    | 'unknown';

export type CrmLifecycle =
    | 'new'
    | 'working'
    | 'qualified'
    | 'opportunity'
    | 'customer'
    | 'expansion'
    | 'nurture'
    | 'disqualified';

export type CrmActivityType =
    | 'note' | 'call' | 'meeting' | 'email_manual'
    | 'stage_change' | 'lifecycle_change' | 'owner_change'
    | 'signup' | 'checklist_step' | 'first_design' | 'first_order'
    | 'shopify_connected' | 'billing_added' | 'funded' | 'enriched'
    | 'email_sent' | 'email_received' | 'email_bounced' | 'email_complained'
    | 'dashboard_view'
    | 'usage';

export interface ICrmStage {
    id: number;
    name: string;
    slug: string;
    rank: number;
    is_won: boolean;
    is_lost: boolean;
    color_type: string;
    active: boolean;
}

export interface ICrmLead {
    id: number;
    /** Archived leads leave every list but the Archived view. */
    archived_at?: string | null;
    archived_reason?: CrmArchiveReason | null;
    archived_by?: number | null;
    archived_note?: string | null;
    /** 0–100 from the sign-up; spam_signals says why. */
    spam_score?: number;
    spam_signals?: string[];
    created_at?: string;
    user_id?: number | null;
    brand_id?: number | null;
    email: string;
    first_name?: string | null;
    last_name?: string | null;
    company_name?: string | null;
    website?: string | null;
    phone?: string | null;
    country?: string | null;
    source: string;
    segment: CrmSegment;
    lifecycle: CrmLifecycle;
    stage_id?: number | null;
    owner_user_id?: number | null;
    score: number;
    score_breakdown?: Record<string, unknown>;
    last_activity_at?: string | null;
    /** Last rep-initiated touch. Null = nobody has reached out yet. */
    last_contacted_at?: string | null;
    do_not_contact?: boolean;
    lost_reason?: string | null;
    socials?: Record<string, string> | null;
    /** Confirmed company facts live under enrichment.company (industry, size, location…). */
    enrichment?: { company?: Record<string, string> } & Record<string, unknown>;
    ecommerce_platform?: string | null;
    journey_keys?: string[] | null;
    is_funded?: boolean | null;
    has_ordered?: boolean | null;
    paid_to_date?: number | null;
    /** Product usage, rolled up every 15 minutes. */
    last_seen_at?: string | null;
    active_days_28?: number | null;
    active_days_prev_28?: number | null;
    sessions_30?: number | null;
    sessions_prev_30?: number | null;
    active_users_28?: number | null;
    engagement?: UsageLabel | null;
    /** Embedded on list reads so a row can draw its journey pips + next move. */
    journeys?: ICrmLeadJourney[] | null;
    /** Heaviest open blocker, merged in live by the list endpoint. */
    top_blocker?: { tag: string; ratio: number } | null;
    /** Who has bookmarked this lead, with their headline. */
    bookmarks?: ICrmLeadBookmark[];
    /** Set by a hard bounce; sending is blocked until the address is changed. */
    email_bounced_at?: string | null;
    /** Oldest unanswered inbound email on the lead. Null once a rep replies. */
    awaiting_reply_since?: string | null;
    account_kind?: string | null;
    disposition?: string | null;
    top_score?: number | null;
    top_journey?: string | null;
    stage?: ICrmStage | null;
    /** List rows only. */
    avatar?: ICrmLeadAvatar | null;
    /** List rows only: designs their brand has made. */
    design_count?: number;
    /** List rows only: minutes in the product over the last 30 days, everyone on their account. */
    active_minutes_30?: number;
    /** List rows only: 'connected' = their Shopify store is live with Wallmates; 'store' = they sell on Shopify, not connected. */
    shopify?: 'connected' | 'store' | null;
    /** List rows only: their latest video demo (a Google Meet call on a rep's calendar), if any. */
    demoed_at?: string | null;
    /** List rows only: 'held' (a demo happened), 'no_show' (they missed it), 'booked' (not known yet); null without a demo. */
    demo_state?: DemoState | null;
}

export interface ICrmActivity {
    id: number;
    lead_id: number;
    actor_user_id?: number | null;
    type: CrmActivityType;
    body?: string | null;
    /** Email rows carry a pointer to the full message; other types leave this empty. */
    metadata?: {
        thread_id?: number;
        message_id?: number;
        direction?: 'in' | 'out';
        subject?: string | null;
        /** Notetaker (Recall.ai) results ride 'meeting' activities. */
        source?: 'recall' | string;
        appointment_id?: number | null;
        meeting_bot_id?: number | null;
        summary?: string | null;
        transcript?: IMeetingTranscriptParagraph[];
        transcript_excerpt?: string;
        word_count?: number;
        /** Website form notes (source 'demo_request') carry every answer they gave. */
        intent?: 'signup' | 'booking' | 'demo_request' | string;
        route?: 'book' | 'self_serve' | string;
        prep?: IDemoPrep;
        attribution?: IFormAttribution;
        /** First and last touch the CRM worked out for this form (newer forms only). */
        touches?: { first: IAttributionTouch; last: IAttributionTouch };
        marketing_consent?: boolean;
        demo_request_id?: number;
    } | null;
    occurred_at: string;
}

/** Where a website form submission came from, as the form recorded it. */
export interface IFormAttribution {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_content?: string;
    utm_term?: string;
    landing_path?: string;
    referrer_origin?: string;
    entry_point?: string;
}

/** Where someone came from, cleaned by the CRM (crm_lead.first_touch / last_touch, migration 24). */
export interface IAttributionTouch {
    source: string | null;
    medium: string | null;
    campaign: string | null;
    content: string | null;
    term: string | null;
    channel: 'paid_social' | 'paid_search' | 'email' | 'social' | 'search' | 'referral' | 'other' | 'direct';
    click?: Partial<Record<'gclid' | 'gbraid' | 'wbraid' | 'msclkid' | 'ttclid' | 'fbclid', string>>;
    landing_path?: string | null;
    referrer?: string | null;
    at?: string | null;
}

export interface ICrmLeadListResult {
    results: ICrmLead[];
    count: number;
    hasNext: boolean;
    hasBefore: boolean;
}

export type CrmInboxView = 'hot' | 'funded' | 'stalled' | 'mine' | 'replied' | 'untouched' | 'archived' | 'possible_spam' | 'clay' | 'shopify' | 'demoed';

/** The picture a People row shows: their brand's logo, an accepted company logo, or a confirmed profile's picture. */
export interface ICrmLeadAvatar {
    url: string;
    kind: 'logo' | 'photo';
    source: string;
}

export type CrmArchiveReason = 'spam' | 'duplicate' | 'test' | 'not_a_fit' | 'other';

/** Spam score cut-off for the "Possible spam" tag (matches the server). */
export const POSSIBLE_SPAM_SCORE = 40;

export interface ICrmArchiveResult {
    archived: number;
    blocked_logins: number;
    /** Logins left alone, and why (staff, sales team). */
    kept_logins: { email: string | null; why: string }[];
}

export interface ICrmLeadFilters {
    /** A triage pile — each is a play, not a generic filter. */
    view?: CrmInboxView | '';
    /** Journey membership, not just the top journey. */
    journey?: JourneyKey | '';
    disposition?: string;
    owner?: 'me' | 'unassigned' | 'all' | '';
    minScore?: number;
    q?: string;
    sort?: 'score' | 'value' | 'newest' | 'untouched' | 'awaiting' | 'last_seen' | '';
    /** Product usage label, or 'today' (used the product in the last 24 hours). */
    usage?: UsageLabel | 'today' | '';
    /** Only bookmarked leads: yours, or anyone's on the team. */
    bookmarks?: 'mine' | 'team' | '';
    /** With the Demoed pile: only leads whose demo was held, missed, or isn't known yet. */
    demo?: DemoState | '';
    page?: number;
    limit?: number;
}

export type UsageLabel = 'power' | 'fading' | 'regular' | 'occasional' | 'quiet' | 'never_returned';

/** A person's bookmark on a lead, with an optional one-line headline. */
export interface ICrmLeadBookmark {
    id: number;
    lead_id: number;
    user_id: number;
    headline: string | null;
    created_at: string;
    updated_at: string;
}

// #region enrichment
/** Where a suggestion came from. */
export type EnrichmentSource = 'website' | 'clay' | 'email';

/**
 * Something found about a lead that nobody has confirmed. field is website | company_name |
 * role_title | country | ecommerce_platform | social:<key> | company:<key>.
 */
export interface ICrmEnrichmentSuggestion {
    id: number;
    lead_id: number;
    field: string;
    value: string;
    source: EnrichmentSource;
    evidence: string | null;
    confidence: number;
    status: 'pending' | 'accepted' | 'dismissed';
    reviewed_by: number | null;
    reviewed_at: string | null;
    created_at: string;
}

export interface ICrmLeadEnrichment {
    suggestions: ICrmEnrichmentSuggestion[];
    company: Record<string, string>;
    last_run: { status: 'running' | 'done' | 'skipped' | 'failed'; started_at: string; finished_at: string | null; found: number; summary?: { notes?: string[] } } | null;
    refresh_after: string | null;
    clay_enabled: boolean;
}

export const USAGE_LABELS: Record<UsageLabel, string> = {
    power: 'Power user',
    regular: 'Regular',
    occasional: 'Occasional',
    fading: 'Fading',
    quiet: 'Gone quiet',
    never_returned: 'Never came back'
};

/** The lead page's Usage tab (crm_lead_engagement). */
export interface ILeadEngagement {
    tracking_since: string | null;
    last_seen_at: string | null;
    active_days_28: number;
    active_days_prev_28: number;
    sessions_30: number;
    sessions_prev_30: number;
    minutes_30: number;
    logins_30: number;
    series: { day: string; sessions: number; minutes: number; people: number }[];
    areas: { area: string; count: number }[];
    people: { id: number; first: string | null; last: string | null; email: string; last_seen: string | null; active_days_28: number; sessions_30: number; last_login_at: string | null; logins_30: number }[];
    logins: { at: string; user_id: number | null; email: string | null; success: boolean; method: string; device: string | null; browser: string | null; os: string | null }[];
}

export type DemoState = 'held' | 'no_show' | 'booked';
export type DemoOutcome = 'held' | 'no_show';

/** A finished Google Meet demo waiting for the rep's Held / No-show. */
export interface IDemoWrapUp {
    event_id: string;
    /** Whose call it was, when it isn't yours (managers see the team's). */
    rep: { id: number; name: string } | null;
    title: string;
    start_at: string;
    end_at: string | null;
    leads: { id: number; name: string; company: string | null }[];
    /** What Google Meet's attendance suggests, when it could tell. */
    suggested: DemoOutcome | null;
    /** e.g. "Google Meet: Kelsey Helm joined for 28 min". */
    note: string | null;
}

export interface ICrmInboxCounts {
    archived?: number;
    possible_spam?: number;
    /** Have a website or work email and Clay hasn't searched them yet. */
    clay?: number;
    /** Their brand has a Shopify store with us (connected or not), or Shopify is confirmed as their platform. */
    shopify?: number;
    demoed?: number;
    demo_held?: number;
    demo_no_show?: number;
    demo_booked?: number;
    hot: number;
    funded: number;
    stalled: number;
    mine: number;
    replied: number;
    untouched: number;
}

export interface ICrmError {
    ok: false;
    message: string;
    status: number;
}

export const isCrmError = (value: unknown): value is ICrmError =>
    typeof value === 'object' && value !== null && (value as ICrmError).ok === false;

/** Human labels for the segment taxonomy, used by filters and badges. */
export const SEGMENT_LABELS: Record<CrmSegment, string> = {
    artist: 'Artist',
    interior_designer: 'Interior Designer',
    wallpaper_brand: 'Wallpaper Brand',
    retailer: 'Retailer',
    architect: 'Architect',
    hospitality: 'Hospitality',
    other: 'Other',
    unknown: 'Unknown'
};

export const LIFECYCLE_LABELS: Record<CrmLifecycle, string> = {
    new: 'New',
    working: 'Working',
    qualified: 'Qualified',
    opportunity: 'Opportunity',
    customer: 'Customer',
    expansion: 'Expansion',
    nurture: 'Nurture',
    disqualified: 'Disqualified'
};


// #region journeys
export type JourneyKey = 'retail' | 'licensing' | 'interior_design' | 'educator';
export type CrmDisposition = 'active' | 'nurturing' | 'disqualified';

export interface ICrmJourneyStage {
    key: string;
    label: string;
    reached: boolean;
    won: boolean;
}

export interface ICrmLeadJourney {
    journey: JourneyKey;
    current_stage_key: string;
    is_won: boolean;
    stages: ICrmJourneyStage[];
    score: number;
    score_breakdown: Record<string, number>;
}

export interface ILeadKpis {
    identity: { brand_name: string | null; website: string | null; logo_url: string | null };
    integrations: { channel: string; enabled: boolean; connected: boolean }[];
    api_keys: { name: string; sandbox: boolean; active: boolean; hosts: string[] }[];
    account: { age_days: number; signed_up_at: string | null; country: string | null; funded: boolean; last_active: string | null };
    catalog: { designs: number; published: number; collections: number; samples: number; mockups?: number };
    retail: { sales: number; orders: number; customers: number; aov: number; last_order_at: string | null; sales_30d: number; sales_prev_30d: number; repeat_rate: number };
    licensing: { licensed_out: number; licensed_in: number; income: number };
    wallmates: { paid: number; balance: number; tokens: number; licensing_fees: number; mockup_revenue?: number };
    series: { months: string[]; sales: number[]; orders: number[] };
}

export interface ILeadPeriodGroup {
    retail: { sales: number; orders: number; customers: number; aov: number; sales_prev: number; repeat_rate: number };
    catalog: { designs: number; published: number; collections: number; samples: number; mockups?: number };
    licensing: { licensed_out: number; licensed_in: number; income: number };
    wallmates: { paid: number; licensing_fees: number; mockup_revenue?: number };
    series: { labels: string[]; sales: number[]; orders: number[] };
}
export type ILeadCommerce = ILeadPeriodGroup;

export interface ICrmOnboardingStep {
    id: number;
    name: string;
    description?: string | null;
    link?: string | null;
    rank: number;
    completed: boolean;
}

/** Response of GET /crm/leads/:id — lead, journeys, KPIs, and onboarding. */
export interface ICrmLeadDetail {
    lead: ICrmLead;
    journeys: ICrmLeadJourney[];
    kpis: ILeadKpis | null;
    onboarding: ICrmOnboardingStep[];
    engagement?: ILeadEngagement | null;
}

export const JOURNEY_LABELS: Record<JourneyKey, string> = {
    retail: 'Retail',
    licensing: 'Licensing',
    interior_design: 'Interior Design',
    educator: 'Educator'
};

export const ACCOUNT_KIND_LABELS: Record<string, string> = {
    store_owner: 'Store Owner',
    artist: 'Artist'
};


// #region profiles
/**
 * Rep-recorded external presence. Deliberately NOT called "channels": a channel
 * is something Wallmates natively integrates with (Shopify today; Patternly and
 * Etsy later) and is detected automatically from brand_channels. These are just
 * links a rep keeps — where the designer markets ("social") and where they
 * already sell that we don't connect to ("shop"). When Etsy becomes a native
 * channel it graduates out of this list.
 */
export type SocialKey = 'instagram' | 'tiktok' | 'pinterest' | 'youtube' | 'x' | 'linkedin' | 'facebook' | 'etsy' | 'spoonflower' | 'patternbank';

export const SOCIAL_PLATFORMS: { key: SocialKey; label: string; base: string; placeholder: string; group: 'social' | 'shop' }[] = [
    { key: 'instagram', label: 'Instagram', base: 'https://instagram.com/', placeholder: '@handle or URL', group: 'social' },
    { key: 'tiktok', label: 'TikTok', base: 'https://tiktok.com/@', placeholder: '@handle or URL', group: 'social' },
    { key: 'pinterest', label: 'Pinterest', base: 'https://pinterest.com/', placeholder: 'profile or URL', group: 'social' },
    // YouTube handles are @-prefixed; legacy /c/ and /channel/ URLs pass through as typed.
    { key: 'youtube', label: 'YouTube', base: 'https://youtube.com/@', placeholder: '@handle or URL', group: 'social' },
    { key: 'x', label: 'X', base: 'https://x.com/', placeholder: '@handle or URL', group: 'social' },
    // LinkedIn splits /in/ (people) and /company/ (brands), so a bare handle can't
    // be resolved reliably — the placeholder asks for the full URL, which passes
    // through untouched. /in/ is only the fallback for a lone handle.
    { key: 'linkedin', label: 'LinkedIn', base: 'https://linkedin.com/in/', placeholder: 'full profile or company URL', group: 'social' },
    { key: 'facebook', label: 'Facebook', base: 'https://facebook.com/', placeholder: 'page name or URL', group: 'social' },
    { key: 'etsy', label: 'Etsy', base: 'https://etsy.com/shop/', placeholder: 'shop name or URL', group: 'shop' },
    { key: 'spoonflower', label: 'Spoonflower', base: 'https://spoonflower.com/profiles/', placeholder: 'profile or URL', group: 'shop' },
    { key: 'patternbank', label: 'Patternbank', base: 'https://patternbank.com/', placeholder: 'profile or URL', group: 'shop' }
];

/** A stored value may be a full URL or a bare handle; resolve either to a link. */
export const socialUrl = (key: SocialKey, value: string): string => {
    const raw = (value || '').trim();
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw)) return raw;
    const platform = SOCIAL_PLATFORMS.find((p) => p.key === key);
    return `${platform?.base ?? 'https://'}${raw.replace(/^@/, '')}`;
};

/**
 * A lead can keep several profiles on one network (the company's LinkedIn page and the person's). The first is
 * stored under the network's key, the rest under key_2, key_3, ... These are the slots, in order, for known networks.
 */
export const socialSlots = (socials: Record<string, string> | null | undefined): { slot: string; key: SocialKey; value: string }[] =>
    Object.entries(socials ?? {})
        .map(([slot, value]) => ({ slot, key: slot.replace(/_\d+$/, '') as SocialKey, n: Number(/_(\d+)$/.exec(slot)?.[1] ?? 1), value }))
        .filter((e) => typeof e.value === 'string' && e.value.trim() && SOCIAL_PLATFORMS.some((p) => p.key === e.key))
        .sort((a, b) => SOCIAL_PLATFORMS.findIndex((p) => p.key === a.key) - SOCIAL_PLATFORMS.findIndex((p) => p.key === b.key) || a.n - b.n)
        .map(({ slot, key, value }) => ({ slot, key, value }));

/** The next empty slot for a network: its own key, then key_2, key_3, ... */
export const nextSocialSlot = (socials: Record<string, string>, key: SocialKey): string => {
    if (!(socials[key] ?? '').trim() && !(key in socials)) return key;
    let n = 2;
    while (`${key}_${n}` in socials) n += 1;
    return `${key}_${n}`;
};

/** Display names for the brand_channels integrations shown on a lead. */
export const INTEGRATION_LABELS: Record<string, string> = {
    SHOPIFY: 'Shopify',
    PATTERNLY: 'Patternly',
    WALLMATES: 'Wallmates',
    API: 'API'
};

// #region partner integrations
/**
 * Partners that integrate through an API key. A brand's key is matched on its
 * name OR its permitted hosts, so a key called "Tilefy Integration" and one
 * merely allowlisting tilefy.ai both resolve to the same partner. The pattern
 * tolerates the "tileify" misspelling that shipped in the key-creation
 * placeholder, so older keys still resolve.
 */
export interface KnownPartner { name: string; site: string; match: RegExp; }

export const KNOWN_PARTNERS: KnownPartner[] = [
    { name: 'Tilefy', site: 'https://tilefy.ai', match: /tile?ify/i }
];

/** Resolves an API key to a known partner, or null when it's a generic key. */
export const matchPartner = (keyName: string, hosts: string[] = []): KnownPartner | null => {
    const haystack = `${keyName || ''} ${(hosts || []).join(' ')}`;
    return KNOWN_PARTNERS.find((p) => p.match.test(haystack)) ?? null;
};

// #region ecommerce platforms
/**
 * The storefront a lead runs when it isn't one we natively integrate with.
 * Recorded by a rep so the aggregate ("31 leads are on Wix") tells us which
 * integration is worth building next. Ordered by how often they actually turn
 * up for surface-pattern and home-decor sellers.
 */
export const ECOMMERCE_PLATFORMS: { value: string; label: string }[] = [
    { value: 'SHOPIFY', label: 'Shopify (not yet connected)' },
    { value: 'WOOCOMMERCE', label: 'WooCommerce' },
    { value: 'WIX', label: 'Wix' },
    { value: 'SQUARESPACE', label: 'Squarespace' },
    { value: 'BIGCOMMERCE', label: 'BigCommerce' },
    { value: 'WEBFLOW', label: 'Webflow' },
    { value: 'BIGCARTEL', label: 'Big Cartel' },
    { value: 'SQUARE_ONLINE', label: 'Square Online' },
    { value: 'ECWID', label: 'Ecwid' },
    { value: 'MAGENTO', label: 'Magento / Adobe Commerce' },
    { value: 'PRESTASHOP', label: 'PrestaShop' },
    { value: 'OPENCART', label: 'OpenCart' },
    { value: 'SALESFORCE', label: 'Salesforce Commerce Cloud' },
    { value: 'WEEBLY', label: 'Weebly' },
    { value: 'GODADDY', label: 'GoDaddy Online Store' },
    { value: 'SHIFT4SHOP', label: 'Shift4Shop' },
    { value: 'ETSY_ONLY', label: 'Etsy only (no own store)' },
    { value: 'SPOONFLOWER', label: 'Spoonflower' },
    { value: 'AMAZON', label: 'Amazon' },
    { value: 'CUSTOM', label: 'Custom / in-house build' },
    { value: 'NONE', label: 'No online store yet' },
    { value: 'OTHER', label: 'Other' }
];

export const platformLabel = (value?: string | null): string =>
    ECOMMERCE_PLATFORMS.find((p) => p.value === value)?.label ?? (value || '');

// #region blockers
/**
 * What is standing between a lead and the next milestone. Rep judgement, not a
 * projection — nothing reconciles these.
 */
export interface ICrmBlocker {
    id: number;
    lead_id: number;
    tag: string;
    label: string;
    /** 1 minor .. 5 dealbreaker. */
    weight: number;
    /** This blocker's share of everything currently blocking THIS lead, 0..1. */
    ratio: number;
    journey: JourneyKey | null;
    note: string | null;
    created_at: string;
    resolved_at: string | null;
}

export interface ICrmBlockerDef {
    key: string;
    label: string;
    hint: string;
    group: 'commercial' | 'product' | 'technical' | 'terms' | 'relationship';
    journeys: JourneyKey[];
}

export interface ICrmBlockerRollup {
    tag: string;
    leads: number;
    weighted: number;
    share: number;
    avg_weight: number;
}

export const BLOCKER_GROUP_LABELS: Record<ICrmBlockerDef['group'], string> = {
    commercial: 'Commercial',
    product: 'Product',
    technical: 'Technical',
    terms: 'Terms',
    relationship: 'Relationship'
};

/**
 * How hard this one issue held them back. The wording is deliberately about
 * impact, not severity in the abstract — a rep is answering "how much did this
 * stop them", which is what makes the ratio meaningful.
 */
export const WEIGHT_LABELS: Record<number, string> = {
    1: 'Minor',
    2: 'Slight',
    3: 'Notable',
    4: 'Serious',
    5: 'Dealbreaker'
};

// #region email
/** Where a CRM email is in its life. */
export type CrmEmailStatus =
    | 'queued' | 'sent' | 'delivered' | 'delayed' | 'bounced' | 'complained' | 'failed' | 'received' | 'scheduled' | 'sending';

export interface ICrmEmailThread {
    id: number;
    lead_id: number | null;
    rep_user_id: number | null;
    counterpart_address: string | null;
    subject: string | null;
    last_message_at: string | null;
    last_direction: 'in' | 'out' | null;
    awaiting_reply: boolean;
    unmatched: boolean;
    last_preview?: string | null;
    snoozed_until?: string | null;
    /** Who besides the owner (rep_user_id) can read it. Managers always can. */
    visibility?: CrmThreadVisibility;
    assigned_by?: number | null;
    assigned_at?: string | null;
}

export type CrmThreadVisibility = 'private' | 'shared' | 'team';

/** Someone with a Wallmates login on the same account as a lead. */
export interface ICrmAccountPerson {
    user_id: number;
    name: string;
    email: string | null;
    /** Their permission tier, e.g. "Admin User". */
    role: string | null;
    joined_at: string | null;
    last_seen: string | null;
    active_days_28: number;
    sessions_30: number;
    /** Their own lead, if they have one. */
    lead_id: number | null;
    /** The person this lead page is about. */
    is_current: boolean;
}

export interface ICrmAccountPeople {
    brand_id: number | null;
    brand_name: string | null;
    people: ICrmAccountPerson[];
}

/** Tabs on the Email screen. */
export type CrmMessagesScope = 'mine' | 'shared' | 'team';
export type CrmMessagesSort = 'newest' | 'oldest' | 'score';

export interface ICrmMessagesFilter {
    scope: CrmMessagesScope;
    journey?: string | null;
    /** A user id, or 'none' for unassigned. */
    owner?: number | 'none' | null;
    sort?: CrmMessagesSort;
}

/** What you may do with one conversation. */
export interface ICrmThreadAccess {
    /** Assign it and choose who sees it. */
    can_manage: boolean;
    /** Reply, mark handled, snooze. */
    can_act: boolean;
    shared_with: number[];
}

/** A teammate, for avatars and the assign/share pickers. */
export interface ICrmTeamMember {
    user_id: number;
    name: string;
    email: string | null;
    avatar_url: string | null;
    level: SalesLevel;
}

/** A thread with enough of its lead to draw a conversation-list row. */
export interface ICrmThreadListItem extends ICrmEmailThread {
    shared_with?: number[];
    lead: {
        id: number;
        first_name: string | null;
        last_name: string | null;
        company_name: string | null;
        email: string;
        owner_user_id: number | null;
        top_journey: string | null;
        top_score?: number | null;
    } | null;
}

export type CrmMessagesView = 'needs_reply' | 'waiting' | 'snoozed' | 'unmatched' | 'all';

export interface ICrmReminder {
    id: number;
    lead_id: number;
    thread_id: number | null;
    user_id: number;
    due_at: string;
    note: string | null;
    done_at: string | null;
}

export interface ICrmEmailMessage {
    id: number;
    thread_id: number;
    lead_id: number | null;
    direction: 'in' | 'out';
    from_address: string;
    to_addresses: string[];
    cc_addresses: string[];
    subject: string | null;
    text_body: string | null;
    status: CrmEmailStatus;
    status_detail: string | null;
    sent_by_user_id: number | null;
    occurred_at: string;
    attachments: { filename: string; content_type: string; size: number; provider_id?: string; attachment_id?: number }[];
    bcc_addresses?: string[];
    /** When it leaves: the end of the undo window, or a scheduled time. */
    send_at?: string | null;
    follow_up_at?: string | null;
    follow_up_done_at?: string | null;
    open_count?: number;
    last_opened_at?: string | null;
    body_doc?: Record<string, unknown> | null;
    visible_text?: string | null;
    opened_at?: string | null;
    is_auto_reply?: boolean;
    /** 'gmail' when pulled from the rep's own mailbox. */
    source?: 'crm' | 'gmail';
}

export interface ICrmLeadEmails {
    threads: ICrmEmailThread[];
    messages: ICrmEmailMessage[];
}

export interface ICrmSendEmailInput {
    subject: string;
    /** Plain text (quick replies). */
    body?: string;
    /** The formatted body from the composer. */
    body_doc?: Record<string, unknown> | null;
    to?: string[];
    cc?: string[];
    bcc?: string[];
    thread_id?: number;
    attachment_ids?: number[];
    /** ISO time; absent = now, after a 10-second undo window. */
    send_at?: string | null;
    follow_up_days?: number | null;
}

/** A file attached to a draft or email. */
export interface IEmailAttachment {
    id: number;
    filename: string;
    content_type: string;
    size: number;
}

/** One of the customer's finished mockups ("attach their work"). */
export interface IBrandMockup {
    id: number;
    design_id: number;
    design_title: string | null;
    mockup_path: string;
    thumbnail_path: string | null;
    created_at: string;
}

export interface IEmailTemplate {
    id: number;
    owner_user_id: number | null;
    name: string;
    subject: string | null;
    body_doc: Record<string, unknown>;
    created_by: number | null;
    use_count?: number;
    updated_at?: string;
    created_at?: string;
    last_used_at?: string | null;
    /** When to use it. */
    description?: string | null;
    /** Free-text folder, e.g. "Follow-up", "Samples", "Pricing". */
    category?: string | null;
    /** Library files attached whenever the template is used. */
    attachments?: ITemplateAttachment[];
    updated_by?: number | null;
    /** The author's name, for team templates. */
    owner_name?: string | null;
}

/** A house asset-library file a rep template attaches each time it's used. */
export interface ITemplateAttachment {
    asset_id: number;
    filename: string;
    content_type: string;
    size: number | null;
    url: string;
}

/** Create / update body for a rep template (PUT takes every field as optional). */
export interface IEmailTemplateInput {
    name: string;
    subject?: string | null;
    body_doc: Record<string, unknown>;
    shared?: boolean;
    description?: string | null;
    category?: string | null;
    attachments?: { asset_id: number }[];
}

export type RepVariableGroup = 'Lead' | 'Company' | 'Me' | 'Dates';

/** A fill-in a rep template can use; `value` is that lead's (with lead_id), else null. */
export interface IRepVariable {
    key: string;
    label: string;
    group: RepVariableGroup;
    sample: string;
    value: string | null;
    fallback: string | null;
}

/** A file in the house asset library, offered to rep templates. */
export interface IRepAsset {
    id: number;
    kind: string;
    title: string;
    url: string;
    mime: string | null;
    /** Safe to show inline in an email body (an image on our own storage). */
    email_safe: boolean;
    width: number | null;
    height: number | null;
    size: number | null;
    alt_text: string | null;
    category: string | null;
}

export interface IRepTemplateCheck { level: 'error' | 'warning' | 'tip'; code: string; message: string }

/** A rep template as the lead would get it (signature and footer included; sample data without a lead). */
export interface IRepTemplatePreview {
    subject: string;
    html: string;
    text: string;
    checks: IRepTemplateCheck[];
    unknown_fields: string[];
}

export interface IEmailDraft {
    id: number;
    thread_id: number | null;
    to_addresses: string[];
    cc_addresses: string[];
    bcc_addresses: string[];
    subject: string | null;
    body_doc: Record<string, unknown> | null;
    attachment_ids: number[];
    updated_at: string;
}

/** What undo gives back so the composer reopens where the rep left off. */
export interface IUnsentEmail {
    thread_id: number | null;
    subject: string;
    body_doc: Record<string, unknown> | null;
    body: string;
    to: string[];
    cc: string[];
    bcc: string[];
    attachments: IEmailAttachment[];
}

export const EMAIL_STATUS_LABELS: Record<CrmEmailStatus, string> = {
    queued: 'Sending',
    sent: 'Sent',
    delivered: 'Delivered',
    delayed: 'Delayed',
    bounced: 'Bounced',
    complained: 'Marked as spam',
    failed: 'Failed',
    received: 'Received',
    scheduled: 'Scheduled',
    sending: 'Sending'
};

// #region calendar
/** A rep's connected Google account. Tokens never leave the server. */
export interface ICalendarAccount {
    id: number;
    email: string;
    status: 'connected' | 'revoked' | 'error';
    status_detail: string | null;
    booking_calendar_id: string | null;
    created_at: string;
    /** Gmail read access is part of the grant (reconnect with mail to add it). */
    mail_scope: boolean;
    send_scope: boolean;
    /** The grant lets the CRM see who joined this rep's Google Meet calls (reconnect to add it). */
    meet_scope?: boolean;
    mail_sync_enabled: boolean;
    mail_backfilled_at: string | null;
    mail_last_synced_at: string | null;
    mail_last_error: string | null;
}

export interface IMailSyncSummary { threads: number; imported: number }

export interface ICrmCalendar {
    id: number;
    account_id: number;
    google_calendar_id: string;
    summary: string | null;
    is_primary: boolean;
    access_role: string | null;
    time_zone: string | null;
    /** Counts as busy time. */
    selected: boolean;
    last_synced_at: string | null;
    last_sync_error: string | null;
    watch_expires_at: string | null;
}

export interface ICalendarSyncSummary {
    calendars: number;
    upserted: number;
    deleted: number;
    failed: number;
}

// #region scheduling
/** A weekly bookable window in the rep's zone. `day` 0 = Sunday, times "HH:MM". */
export interface IAvailabilityWindow { day: number; start: string; end: string }

export interface IAvailability {
    user_id: number;
    slug: string;
    time_zone: string;
    windows: IAvailabilityWindow[];
    buffer_before_min: number;
    buffer_after_min: number;
    min_notice_min: number;
    horizon_days: number;
    max_per_day: number | null;
    booking_account_id: number | null;
    welcome: string | null;
    /** Opt-in: a Recall.ai notetaker is scheduled for every new booking with a Meet link. */
    record_meetings: boolean;
}

export type IAvailabilityPatch = Partial<Omit<IAvailability, 'user_id'>>;

export type MeetingLocation = 'meet' | 'phone';

export interface IMeetingType {
    id: number;
    user_id: number;
    name: string;
    duration_min: number;
    description: string | null;
    location: MeetingLocation;
    active: boolean;
    sort_order: number;
}

export type IMeetingTypePatch = Partial<Pick<IMeetingType, 'name' | 'duration_min' | 'description' | 'location' | 'active' | 'sort_order'>>;

export interface IAvailabilityOverride {
    id: number;
    user_id: number;
    kind: 'block' | 'open';
    start_at: string;
    end_at: string;
    note: string | null;
}

export interface ISchedulingSettings {
    availability: IAvailability;
    meeting_types: IMeetingType[];
    booking_url: string;
    rep_name: string;
    writes_to: { account_id: number; email: string; calendar_id: string } | null;
}

export type AppointmentStatus = 'scheduled' | 'cancelled' | 'completed' | 'no_show';

export interface IAppointment {
    id: number;
    user_id: number;
    lead_id: number | null;
    meeting_type_id: number | null;
    title: string;
    start_at: string;
    end_at: string;
    invitee_name: string;
    invitee_email: string;
    invitee_time_zone: string | null;
    notes: string | null;
    status: AppointmentStatus;
    source: 'booking_page' | 'rep';
    meet_link: string | null;
    google_event_id: string | null;
    cancelled_at: string | null;
    cancelled_by: 'invitee' | 'rep' | 'google' | 'system' | null;
    cancel_reason: string | null;
    created_at: string;
    /** Set when the booking came from the website demo form. */
    demo_request_id?: number | null;
    prep?: IDemoPrep | null;
    /** Confirm-to-keep: the deadline (null when none was asked), and when/who confirmed. */
    confirm_by?: string | null;
    confirmed_at?: string | null;
    confirmed_by?: 'invitee' | 'rep' | 'auto' | null;
    /** The deadline passed and the meeting was kept (booked by a rep): flagged. */
    confirm_lapsed_at?: string | null;
}

export type ConfirmationState = 'confirmed' | 'awaiting' | 'lapsed' | null;

/** How a scheduled meeting stands on confirmation; null when it isn't scheduled or none was asked. */
export const confirmationState = (a: Pick<IAppointment, 'status' | 'confirm_by' | 'confirmed_at' | 'confirmed_by' | 'confirm_lapsed_at'>): ConfirmationState => {
    if (a.status !== 'scheduled') return null;
    if (a.confirmed_at) return a.confirmed_by === 'auto' ? null : 'confirmed';
    if (a.confirm_lapsed_at) return 'lapsed';
    return a.confirm_by ? 'awaiting' : null;
};

/** A meeting email: reminders, confirm nudges, after-call follow-ups. */
export interface IAppointmentNotice {
    id: number;
    appointment_id: number;
    kind: 'reminder_day' | 'reminder_hour' | 'confirm_nudge' | 'after_no_show' | 'after_held';
    status: 'queued' | 'sending' | 'sent' | 'skipped' | 'cancelled' | 'failed';
    send_after: string;
    sent_at: string | null;
    template_key: string | null;
    detail: string | null;
}

/** What someone told us on the website demo form, shown to the rep before the call. */
export interface IDemoPrep {
    company: string;
    segment: string;
    /** Every role they picked, and the products they are interested in (v2 forms). */
    personas?: string[];
    products?: string[];
    material?: string | null;
    /** Null when they booked straight away and skipped the qualifying questions. */
    goal: string | null;
    platform: string | null;
    volume: string | null;
    timeline: string | null;
    website: string | null;
    country: string;
    note: string | null;
    preferred_timing: string | null;
    source: string | null;
}

/** One speaker turn of a notetaker transcript. Times are seconds from the start of the recording. */
export interface IMeetingTranscriptParagraph {
    speaker: string | null;
    start: number | null;
    end: number | null;
    text: string;
}

/** A Recall.ai notetaker sent to a meeting. Statuses are Recall's own codes plus pending / failed / cancelled. */
export interface IMeetingBot {
    id: number;
    created_at?: string;
    updated_at?: string;
    appointment_id: number | null;
    lead_id: number | null;
    meeting_url: string;
    join_at: string | null;
    status: string;
    status_sub_code: string | null;
    status_at: string | null;
    transcript_status: 'requested' | 'done' | 'failed' | null;
    summary: string | null;
    error: string | null;
    has_transcript: boolean;
    wrapup_status?: 'pending' | 'done' | 'skipped' | null;
    wrapup_at?: string | null;
}

/** What the model pulled out of a recorded call. A proposal until the rep wraps up. */
export interface IMeetingActionItem {
    text: string;
    owner: 'rep' | 'prospect';
    due_in_days: number | null;
}

export interface IMeetingAnalysis {
    summary: string;
    key_points: string[];
    objections: string[];
    decisions: string[];
    action_items: IMeetingActionItem[];
    sentiment: 'positive' | 'neutral' | 'negative' | null;
    next_step: string | null;
    /** Who the rep was talking to, as heard on the call. */
    prospect?: { name: string | null; company: string | null } | null;
}

/** One card in the rep's wrap-up queue. */
export interface IPendingWrapUp {
    bot: IMeetingBot;
    analysis: IMeetingAnalysis | null;
    transcript_excerpt: string;
    appointment: { id: number; title: string; start_at: string; end_at: string; invitee_name: string; invitee_email: string; status: string } | null;
    lead: { id: number; name: string; company_name: string | null } | null;
    wrapup_snoozed_until: string | null;
}

export interface IWrapUpSubmission {
    outcome: 'completed' | 'no_show' | null;
    summary: string;
    notes: string[];
    tasks: { text: string; due_at: string | null }[];
    lead_id?: number | null;
    /** Who the call was with, when no lead is attached yet. Matched by email or created as a new lead. */
    person?: { name?: string | null; email?: string | null; company?: string | null } | null;
}

export interface IWrapUpResult {
    bot: IMeetingBot;
    tasks_created: number;
    activity_written: boolean;
}

export interface IMeetingBotTranscript {
    id: number;
    summary: string | null;
    transcript: IMeetingTranscriptParagraph[];
    transcript_text: string;
}

export interface ILaunchBotInput {
    appointment_id?: number | null;
    meeting_url?: string | null;
    lead_id?: number | null;
    join_at?: string | null;
}

export const MEETING_BOT_STATUS_LABELS: Record<string, string> = {
    pending: 'Setting up', scheduled: 'Scheduled', joining_call: 'Joining', in_waiting_room: 'Waiting to be admitted',
    in_call_not_recording: 'In call, not recording', recording_permission_allowed: 'Allowed to record', recording_permission_denied: 'Recording declined',
    in_call_recording: 'Recording', call_ended: 'Processing', done: 'Finished', fatal: 'Could not record', failed: 'Could not be sent', cancelled: 'Removed'
};

export interface IAgendaDay {
    date: string;
    windows: { start_at: string; end_at: string }[];
    busy: { start_at: string; end_at: string; all_day: boolean }[];
    /** Google events with what they say. Older servers send none. */
    events?: IAgendaEvent[];
    appointments: IAppointment[];
}

/** One Google event on a rep's calendar. Private ones read "Busy" to anyone but their owner. */
export interface IAgendaEvent {
    calendar_id: number;
    google_event_id: string;
    calendar_name: string | null;
    start_at: string;
    end_at: string;
    all_day: boolean;
    /** Marked "free" in Google: shown, never blocks booking. */
    free: boolean;
    title: string;
    description: string | null;
    location: string | null;
    html_link: string | null;
    meet_link: string | null;
    attendees: { email: string; name: string | null; response: string | null; organizer: boolean }[];
    organizer_email: string | null;
    is_organizer: boolean;
    self_response: string | null;
    recurring: boolean;
    private: boolean;
    can_edit: boolean;
}

/** A teammate whose calendar can be shown. */
export interface ICalendarTeammate {
    user_id: number;
    name: string;
    email: string | null;
    avatar_url: string | null;
    is_me: boolean;
    has_calendar: boolean;
}

/** Create or change an event. Timed: ISO instants. All-day: YYYY-MM-DD dates, end inclusive. */
export interface IEventInput {
    user_id?: number;
    title: string;
    start: string;
    end: string;
    all_day: boolean;
    description: string | null;
    location: string | null;
    attendees: string[];
    with_meet: boolean;
    notify: boolean;
}

export interface IAgenda {
    /** Whose calendar this is. */
    user_id?: number;
    time_zone: string;
    from: string;
    to: string;
    days: IAgendaDay[];
    reminders: { id: number; lead_id: number; thread_id: number | null; due_at: string; note: string | null }[];
}

export interface ISlots { time_zone: string; slots: string[] }

export interface IBookForLeadInput {
    lead_id: number | null;
    meeting_type_id: number;
    start_at: string;
    invitee_name: string;
    invitee_email: string;
    notes?: string | null;
}

// #region public booking (no auth)
export interface IPublicBookingProfile {
    slug: string;
    rep_name: string;
    welcome: string | null;
    time_zone: string;
    meeting_types: { id: number; name: string; duration_min: number; description: string | null; location: MeetingLocation }[];
}

export interface IPublicBooking {
    id: number;
    title: string;
    start_at: string;
    end_at: string;
    status: AppointmentStatus;
    invitee_name: string;
    invitee_email: string;
    invitee_time_zone: string | null;
    meet_link: string | null;
    meeting_type_id: number | null;
    rep_name: string;
    slug: string | null;
    manage_token?: string;
    confirmed_at?: string | null;
    confirm_by?: string | null;
    needs_confirmation?: boolean;
    /** Unconfirmed self-booked meetings are released at the deadline. */
    releases_if_unconfirmed?: boolean;
}

// #region message templates (Sales → Templates)
export type TemplateEditable = 'full' | 'subject' | 'none';

export interface IMergeField { key: string; label: string; sample: string }

export interface IMessageTemplate {
    key: string;
    group: string;
    name: string;
    fires: string;
    to: string;
    editable: TemplateEditable;
    can_disable: boolean;
    disable_note?: string;
    fields: IMergeField[];
    /** Built-in copy. */
    subject: string;
    body: string;
    effective: { subject: string; body: string; enabled: boolean; customized: boolean };
    override: { subject: string | null; body: string | null; enabled: boolean; updated_at?: string; updated_by?: number | null } | null;
    problems: { subject?: string; body?: string };
}

export interface ITemplatePreview {
    subject: string;
    html: string;
    text: string;
    available: boolean;
    problems: { subject?: string; body?: string };
}

export const APPOINTMENT_STATUS_LABELS: Record<AppointmentStatus, string> = {
    scheduled: 'Scheduled', cancelled: 'Cancelled', completed: 'Completed', no_show: 'No-show'
};

// #region tasks
export type TaskKind = 'task' | 'snooze';

export interface ICrmTask {
    id: number;
    created_at: string;
    updated_at?: string;
    user_id: number;
    created_by: number | null;
    kind: TaskKind;
    lead_id: number | null;
    thread_id: number | null;
    appointment_id: number | null;
    journey: JourneyKey | null;
    note: string;
    due_at: string | null;
    done_at: string | null;
    lead: { id: number; name: string; email: string; company_name: string | null } | null;
    appointment: { id: number; title: string; start_at: string } | null;
}

export interface ICrmTaskInput {
    note: string;
    due_at?: string | null;
    lead_id?: number | null;
    appointment_id?: number | null;
    journey?: JourneyKey | null;
    /** Who owes it; default the person adding it. */
    assignee_user_id?: number | null;
}

export interface ICrmTaskPatch {
    note?: string;
    due_at?: string | null;
    lead_id?: number | null;
    journey?: JourneyKey | null;
    done?: boolean;
    /** Hand it to a teammate. */
    assignee_user_id?: number;
}

// #region team booking links
export type TeamMode = 'round_robin' | 'collective';

export interface ITeamMember {
    id: number;
    team_id: number;
    user_id: number;
    weight: number;
    journeys: JourneyKey[];
    last_assigned_at: string | null;
    name: string;
    email: string | null;
    /** Has a connected Google account with a ticked, synced calendar, so their busy time is known and they can take bookings. */
    ready: boolean;
    /** Why they sit out: no Google account, or no calendar ticked yet. Null when ready. */
    not_ready_reason?: 'no_google' | 'no_calendar' | null;
    bookings_30d: number;
}

export interface ITeamView {
    id: number;
    slug: string;
    name: string;
    mode: TeamMode;
    journey: JourneyKey | null;
    welcome: string | null;
    active: boolean;
    members: ITeamMember[];
    meeting_types: { id: number; name: string; duration_min: number; description: string | null; location: MeetingLocation; active: boolean }[];
    booking_url: string;
    bookings_30d: number;
}

export interface ITeamPatch { name?: string; slug?: string; mode?: TeamMode; journey?: JourneyKey | null; welcome?: string | null; active?: boolean }

/** How a group of conversations did over the period: a rep (key = user id, null = unassigned) or a journey (key, null = none). */
export interface ICrmEmailInsightRow {
    key: number | string | null;
    sent: number;
    received: number;
    answered: number;
    median_reply_minutes: number | null;
    within_day_pct: number | null;
    response_rate_pct: number | null;
    waiting: number;
}

/** Email analytics for the Messages screen (only conversations the viewer may see). */
export interface ICrmEmailInsights {
    from: string;
    to: string;
    days: number;
    totals: ICrmEmailInsightRow & { opened: number; open_tracked: number; open_rate_pct: number | null };
    waiting: { under_1d: number; d1_3: number; d3_7: number; over_7d: number };
    by_rep: ICrmEmailInsightRow[];
    by_journey: ICrmEmailInsightRow[];
    weekly: { week: string; sent: number; received: number }[];
}

// ---- Email signatures (GET/PUT /crm/signature, POST /crm/signature/preview, GET /crm/signature/stats) ----

export type SocialNetwork = 'instagram' | 'pinterest' | 'tiktok' | 'linkedin' | 'facebook' | 'youtube';

/** The company's one banner: an email-safe image from the asset library, with a link and optional dates. */
export interface ISignatureBanner {
    /** Server-made, stable while the banner exists. */
    id: string;
    /** From the asset library (GET /email-assets); the server fills image_url/width/height. */
    asset_id: number | null;
    image_url: string;
    alt: string;
    /** https only. */
    href: string;
    /** Displayed size; width <= 480. */
    width: number;
    height: number;
    /** ISO dates; outside them the banner is not shown. */
    starts_at: string | null;
    ends_at: string | null;
}

/** What PUT /signature takes for the banner. */
export interface ISignatureBannerInput {
    asset_id: number | null;
    alt?: string;
    href: string;
    starts_at?: string | null;
    ends_at?: string | null;
    width?: number;
}

export type SignatureReplyStyle = 'compact' | 'full' | 'none';

/** The company part of every rep's signature, set by managers and coloured by the brand kit. */
export interface ICompanySignature {
    company_name: string;
    /** The brand kit's logo (PNG). */
    show_logo: boolean;
    /** One line, <= 90 chars. */
    tagline: string | null;
    website: { label: string; url: string } | null;
    /** <= 6, https. */
    socials: { network: SocialNetwork; url: string }[];
    banner: ISignatureBanner | null;
    /** The signature on replies in a thread. */
    reply_style: SignatureReplyStyle;
    track_clicks: boolean;
}

export type ICompanySignaturePatch = Partial<Omit<ICompanySignature, 'banner'>> & { banner?: ISignatureBannerInput | null };

/** The rep's own part of the signature, all on the access summary (GET/PUT /crm/me). */
export interface IRepSignatureFields {
    from_name: string | null;
    signature_title: string | null;
    /** Their booking link. */
    calendar_link: string | null;
    /** <= 40; null shows "Book a call". */
    booking_label: string | null;
    /** Digits, spaces, + ( ) - . only, <= 40. */
    phone: string | null;
    linkedin_url: string | null;
    /** Set by the photo endpoints, never by PUT /me. */
    signature_photo_url: string | null;
    show_photo: boolean;
}

export interface ICompanySignatureResponse {
    settings: ICompanySignature;
    can_edit: boolean;
    logo_url: string | null;
    accent: string;
}

export interface IRenderedSignature { html: string; text: string }

export interface ISignaturePreview {
    /** First email in a thread. */
    full: IRenderedSignature;
    /** Replies; null when reply_style is 'none', equals full when 'full'. */
    compact: IRenderedSignature | null;
    /** The full signature with rep-level tracked links and no lead, for pasting into Gmail settings. */
    gmail: IRenderedSignature;
}

/** Unsaved drafts to preview; anything omitted renders as saved. */
export interface ISignaturePreviewInput {
    rep?: Partial<IRepSignatureFields>;
    settings?: Partial<ICompanySignature>;
}

export interface ISignatureStats {
    days: number;
    total: number;
    by_kind: { banner: number; booking: number; website: number; linkedin: number; social: number };
    by_rep: { user_id: number; name: string | null; clicks: number; booking: number }[];
    by_banner: { id: string; alt: string; clicks: number }[];
}
